import type { JarvisRunRecord, JarvisRunStatus } from "../types/jarvis";

interface ListResponse {
  ok: boolean;
  runs?: JarvisRunRecord[];
  error?: string;
}

interface MutateResponse {
  ok: boolean;
  run?: JarvisRunRecord;
  error?: string;
}

const RUN_ID_HEADER = "x-pericles-run-id";

function buildCorrelationRunId(): string {
  const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 17);
  const random = Math.random().toString(16).slice(2, 10).padEnd(8, "0");
  return `run_${timestamp}_${random}`;
}

function handleError(response: Response, payload: { error?: string }, fallback: string): never {
  const message = payload?.error ?? `${fallback} (status ${response.status})`;
  throw new Error(message);
}

export interface ListJarvisRunsFilters {
  workItemId?: string;
  statuses?: JarvisRunStatus[];
  from?: string;
  to?: string;
}

export async function listJarvisRuns(filters: ListJarvisRunsFilters = {}): Promise<JarvisRunRecord[]> {
  const params = new URLSearchParams();
  if (filters.workItemId) params.set("workItemId", filters.workItemId);
  if (filters.statuses?.length) params.set("status", filters.statuses.join(","));
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);

  const qs = params.toString();
  const response = await fetch(`/api/jarvis/runs${qs ? `?${qs}` : ""}`);
  const payload = (await response.json()) as ListResponse;

  if (!response.ok || payload.ok === false) {
    handleError(response, payload, "Failed to fetch Jarvis runs");
  }

  return payload.runs ?? [];
}

export interface TriggerJarvisRunInput {
  environment: string;
  spec: unknown;
  dryRun?: boolean;
  provider?: string;
  providerEnv?: Record<string, string>;
  workspaceId?: string;
}

export async function triggerJarvisRun(workItemId: string, input: TriggerJarvisRunInput): Promise<JarvisRunRecord> {
  const correlationRunId = buildCorrelationRunId();
  const response = await fetch(`/api/work-items/${workItemId}/jarvis-runs`, {
    method: "POST",
    headers: { "Content-Type": "application/json", [RUN_ID_HEADER]: correlationRunId },
    body: JSON.stringify(input),
  });

  const payload = (await response.json()) as MutateResponse;

  if (!response.ok || payload.ok === false || !payload.run) {
    handleError(response, payload, "Failed to start Jarvis run");
  }

  return payload.run;
}

export async function fetchJarvisRun(runId: string): Promise<JarvisRunRecord> {
  const response = await fetch(`/api/jarvis/runs/${runId}`);
  const payload = (await response.json()) as MutateResponse;

  if (!response.ok || payload.ok === false || !payload.run) {
    handleError(response, payload, "Failed to load Jarvis run");
  }

  return payload.run;
}
